import { useEffect, useState } from 'react';

/**
 * Slajdy przewodnika — zrzuty ekranu nagrane przed wdrożeniem na staging
 * (`e2e/guides/record.mjs`, job w workflow „App Staging Deploy”). Leżą
 * w katalogu statycznym buildu frontu: `/guides/manifest.json`
 * i `/guides/slides/<klucz przełącznika>/<n>.png`.
 *
 * Brak slajdów (dev, lokalnie bez nagrania) to nie błąd: panel działa bez
 * miniatur — opis i podświetlenie na żywo — z dopiskiem, że slajdów nie ma.
 */

export type SlideInfo = {
  /** Numer kroku od 1 — jak w nazwie pliku. */
  n: number;
  /** Ścieżka względem katalogu przewodników (`slides/<klucz>/<n>.png`). */
  file: string;
  width: number;
  height: number;
};

export type SlidesManifest = {
  appVersion: string | null;
  commit: string | null;
  recordedAt: string | null;
  guides: Record<string, { steps: SlideInfo[] }>;
};

/** Katalog przewodników w buildzie (z `PUBLIC_URL`, jak reszta plików statycznych). */
export const GUIDES_BASE = `${process.env.PUBLIC_URL ?? ''}/guides`;

const isSlide = (v: unknown): v is SlideInfo => {
  const s = v as Partial<SlideInfo> | null;
  return (
    !!s &&
    typeof s.n === 'number' &&
    typeof s.file === 'string' &&
    /^slides\/[A-Za-z0-9]+\/\d+(-\d+)?\.png$/.test(s.file) &&
    typeof s.width === 'number' &&
    typeof s.height === 'number'
  );
};

/** Manifest w bezpiecznym kształcie; coś innego (np. strona HTML serwera) = brak slajdów. */
export function normalizeManifest(data: unknown): SlidesManifest | null {
  const d = data as Record<string, unknown> | null;
  if (!d || typeof d !== 'object' || !d.guides || typeof d.guides !== 'object') return null;
  const guides: SlidesManifest['guides'] = {};
  for (const [key, value] of Object.entries(d.guides as Record<string, unknown>)) {
    const steps = (value as { steps?: unknown } | null)?.steps;
    if (Array.isArray(steps)) guides[key] = { steps: steps.filter(isSlide) };
  }
  const str = (v: unknown) => (typeof v === 'string' ? v : null);
  return { appVersion: str(d.appVersion), commit: str(d.commit), recordedAt: str(d.recordedAt), guides };
}

let cache: Promise<SlidesManifest | null> | null = null;

/** Manifest — jeden odczyt na sesję; brak albo błąd = `null` (bez slajdów). */
export function loadSlidesManifest(): Promise<SlidesManifest | null> {
  if (!cache) {
    cache = fetch(`${GUIDES_BASE}/manifest.json`, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => normalizeManifest(data))
      .catch(() => null);
  }
  return cache;
}

/** Tylko dla testów. */
export function resetSlidesCache() {
  cache = null;
}

/** Slajd kroku (od 0) albo `null`. */
export function slideFor(
  manifest: SlidesManifest | null,
  flagKey: string,
  stepIndex: number,
): (SlideInfo & { url: string }) | null {
  const s = manifest?.guides[flagKey]?.steps.find((x) => x.n === stepIndex + 1);
  return s ? { ...s, url: `${GUIDES_BASE}/${s.file}` } : null;
}

export type SlidesStatus = { loading: boolean; manifest: SlidesManifest | null };

export function useSlidesManifest(): SlidesStatus {
  const [status, setStatus] = useState<SlidesStatus>({ loading: true, manifest: null });
  useEffect(() => {
    let active = true;
    void loadSlidesManifest().then((manifest) => {
      if (active) setStatus({ loading: false, manifest });
    });
    return () => {
      active = false;
    };
  }, []);
  return status;
}
